<template>
  <div class="car-in-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('carInManage.page.title')" :subtitle="$t('carInManage.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：停车场区域选择 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-panel ds-role-tree">
          <div class="ds-role-tree__title">{{ $t('carInManage.parkingArea') }}</div>
          <ul class="ds-role-tree__list">
            <li v-for="(item, index) in carInManageInfo.parkingAreas" :key="index" class="ds-role-tree__item"
              :class="{ 'is-active': carInManageInfo.conditions.paNum === item.num }" @click="swatchParkingArea(item)">
              {{ item.num }}
            </li>
          </ul>
          <div v-if="!carInManageInfo.parkingAreas.length" class="ds-role-tree__empty">
            {{ $t('carInManage.page.emptyArea') }}
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="carInManageInfo.conditions.carNum" clearable class="w-full"
                :placeholder="$t('carInManage.inputPlateNumber')" @keyup.enter.native="queryCarInoutMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="carInManageInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('carInManage.selectVehicleStatus')">
                <el-option value="" :label="$t('carInManage.selectVehicleStatus')" />
                <el-option value="100300" :label="$t('carInManage.inStatus')" />
                <el-option value="100400" :label="$t('carInManage.paymentComplete')" />
                <el-option value="100500" :label="$t('carInManage.exitStatus')" />
                <el-option value="100600" :label="$t('carInManage.timeoutRepayment')" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="carInManageInfo.conditions.carType" clearable class="w-full"
                :placeholder="$t('carInManage.selectPlateType')">
                <el-option value="" :label="$t('carInManage.selectPlateType')" />
                <el-option value="T" :label="$t('carInManage.temporaryVehicle')" />
                <el-option value="S" :label="$t('carInManage.forSaleVehicle')" />
                <el-option value="H" :label="$t('carInManage.monthlyRental')" />
                <el-option value="W" :label="$t('carInManage.whitelist')" />
                <el-option value="B" :label="$t('carInManage.blacklist')" />
                <el-option value="I" :label="$t('carInManage.internalVehicle')" />
                <el-option value="NM" :label="$t('carInManage.freeVehicle')" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="carInManageInfo.conditions.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('carInManage.enterStartTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="carInManageInfo.conditions.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('carInManage.enterEndTime')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryCarInoutMethod">
              {{ $t('carInManage.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetCarInoutMethod">
              {{ $t('carInManage.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="loadCarIns">
            <template #left>
              <span class="ds-panel__title">{{ $t('carInManage.parkedVehicles') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="carInManageInfo.carIns" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('carInManage.page.empty')" @retry="loadCarIns">
            <el-table-column prop="ciId" :label="$t('carInManage.inoutId')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.ciId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('carInManage.vehicleStatus')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag size="sm" type="primary" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column prop="carNum" :label="$t('carInManage.plateNumber')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="paNum" :label="$t('carInManage.parkingLot')" align="center" width="120" />
            <el-table-column prop="feeName" :label="$t('carInManage.billingRule')" align="center" min-width="130"
              show-overflow-tooltip />
            <el-table-column prop="carTypeName" :label="$t('carInManage.plateType')" align="center" width="110" />
            <el-table-column prop="inTime" :label="$t('carInManage.entryTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInManage.parkingDuration')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.hours }} {{ $t('carInManage.hour') }} {{ scope.row.min }} {{ $t('carInManage.minute') }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInManage.parkingFee')" align="right" width="110">
              <template slot-scope="scope">
                <span v-if="scope.row.carType !== '1001'" class="ds-num">{{ scope.row.payCharge }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="pagination.currentPage" :size="pagination.pageSize" :total="pagination.total"
            @current-change="handleCurrentChange" @size-change="handleSizeChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listParkingAreas, listCarIns } from '@/api/car/carInManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'CarInManageList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      carInManageInfo: {
        parkingAreas: [],
        carIns: [],
        conditions: {
          state: '',
          carNum: '',
          carType: '',
          startTime: '',
          endTime: '',
          paNum: '',
          iotApiCode: 'listCarInParkingAreaBmoImpl',
          page: 1,
          row: 10,
          communityId: ''
        }
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.carInManageInfo.conditions.communityId = this.communityId
    this.loadParkingAreas()
  },
  methods: {
    // 加载停车场区域
    async loadParkingAreas() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.communityId
        }
        const response = await listParkingAreas(params)
        this.carInManageInfo.parkingAreas = response.parkingAreas || []

        if (this.carInManageInfo.parkingAreas.length > 0) {
          this.carInManageInfo.conditions.paNum = this.carInManageInfo.parkingAreas[0].num
          this.loadCarIns()
        }
      } catch (error) {
        this.$message.error(this.$t('common.loadFailed'))
      }
    },
    swatchParkingArea(parkingArea) {
      this.carInManageInfo.conditions.paNum = parkingArea.num
      this.pagination.currentPage = 1
      this.loadCarIns()
    },

    // 加载在场车辆
    async loadCarIns() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.carInManageInfo.conditions,
          page: this.pagination.currentPage,
          row: this.pagination.pageSize
        }
        params.carNum = params.carNum ? params.carNum.trim() : ''

        const response = await listCarIns(params)
        this.carInManageInfo.carIns = response.data || []
        this.pagination.total = response.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('common.loadFailed')
        this.$message.error(this.$t('common.loadFailed'))
      } finally {
        this.loading = false
      }
    },

    // 切换停车场区域
    handleAreaChange(val) {
      this.carInManageInfo.conditions.paNum = val
      this.loadCarIns()
    },

    // 查询
    queryCarInoutMethod() {
      this.pagination.currentPage = 1
      this.loadCarIns()
    },

    // 重置
    resetCarInoutMethod() {
      this.carInManageInfo.conditions = {
        ...this.carInManageInfo.conditions,
        state: '',
        carNum: '',
        carType: '',
        startTime: '',
        endTime: ''
      }
      this.pagination.currentPage = 1
      this.loadCarIns()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.loadCarIns()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadCarIns()
    }
  }
}
</script>

<style lang="scss" scoped>
.car-in-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-role-tree {
  padding: var(--ds-spacing-md) 0;

  &__title {
    padding: 0 var(--ds-spacing-lg) var(--ds-spacing-sm);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-600);
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__item {
    padding: 10px var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    text-align: center;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary-sub);
      border-left-color: var(--ds-primary);
      color: var(--ds-primary);
      font-weight: 600;
    }
  }

  &__empty {
    padding: var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
    text-align: center;
  }
}
</style>
